<template>
  <div class="text-white font-bold p-0 bg-primary-default">
    <div class="ml-auto w-11/12">
      <div class="grid grid-rows md:grid-cols md:grid-rows-1 md:grid-cols-2">
        <div class="py-4">
          <h1 class="text-2xl font-bold tracking-tight text-white m-0 pl-1">Apparatenoverzicht</h1>
        </div>
      </div>
    </div>
  </div>
  <section class="pumpOverview pt-8 mx-auto w-10/12 min-h-screen">
    <div>
      <pump-search
        @reset-filters="computeOverview"
        @force-reset-filters="forceResetFilters"
        @search-pump="searchPump"
        :isPumpOverview="isPumpOverview"
        :pumpsRequireMaintenance="pumpsRequireMaintenance"
      />
      <v-table class="w-full bg-white mb-4">
        <thead>
          <v-header-row
            :data="pumpsHeader"
            :show-sort="true"
            :page="'Pompenoverzicht'"
            @sort-by-field="sortPumpsByField"
          />
        </thead>
        <v-pump-overview-row
          v-for="(row, index) in updatedPumps"
          :key="index"
          :data="row"
          :index="index"
          :user-warehouse-id="userWarehouseId"
          @retrieve="retrievePump"
          @reload-overview="reloadOverview"
          @force-reset-filters="forceResetFilters"
        />
      </v-table>
    </div>
    <div class="underline mb-6 text-pdsPrimaryColor cursor-pointer">
      <span @click.prevent="toggleReportAbnormality">
        Klopt er iets niet aan één van de apparaten in uw depot? Stuur via deze link een bericht naar Mediq.
      </span>
    </div>
    <div v-show="showReportAbnormality">
      <v-table>
        <tr>
          <th />
        </tr>
        <tr>
          <th v class="text-tdefault-color">
            Selecteer de desbetreffende apparaat en noteer in het tekstvak uw probleem. Klik op 'Versturen' om uw verzoek te
            voltooien.
          </th>
        </tr>
        <tr>
          <td>
            <div class="pumpOverview__selectPump">
              <v-select-group
                v-model="currentPumpId"
                selectid="PumpId"
                :initselected="'0'"
                placeholder="Selecteer apparaat"
                @change="setSelectedPumpId($event)"
              >
                <option
                  v-for="(item, index) in overviewPumps.allPumps"
                  :key="index + 1"
                  :data-index="index + 1"
                  :value="item.equipmentID"
                >
                  {{ item.pumpDesc }} {{ item.serialNo }}
                </option>
              </v-select-group>
            </div>
          </td>
        </tr>
        <tr>
          <td>
            <textarea
              id="pumpOverview-ReportAbnormality"
              v-model="abnormalityRemark"
              class="w-full border border-primary-default rounded-md min-h-52 p-4"
              placeholder="Typ hier uw bericht…"
              :overwriteValue="abnormalityRemark"
              @input="setRemarks($event.target.value)"
            />
          </td>
        </tr>
        <tr>
          <td>
            <button
              @click.prevent="reportAbnormality()"
              class="flex justify-center p-2 md:px-4 text-xs md:text-base font-medium text-white items-center bg-primary-default border border-primary-default rounded-full text-nowrap float-right w-auto"
              :class="{ 'bg-slate-400 border-slate-400': abnormalityRemark == '' }"
              :disabled="abnormalityRemark == ''"
            >
              Versturen
            </button>
          </td>
        </tr>
      </v-table>
    </div>
    <v-overlay v-if="retrieveOverlay && Object.keys(selectedPumpToRetrieve).length !== 0">
      <v-pump-retrieve-form
        :data="selectedPumpToRetrieve"
        @success="closeRetrieveOverlay"
        @close-modal="closeRetrieveOverlay"
      />
    </v-overlay>
  </section>
</template>

<script>
import axios from 'axios';
import VPumpOverviewRow from './PumpOverviewRow';
import VTable from '@/components/shared/table/Table';
import VHeaderRow from '@/components/shared/table/rows/HeaderRow';
import VPumpRetrieveForm from './PumpRetrieveForm';
import PumpSearch from './PumpSearch';
import { fetchQuery } from '@/services/messaging/fetchQuery';
import { activePpwRecId$ } from '@/services';
import { currentUserIdentity$ } from '@/services';
import { useStore, mapMutations } from 'vuex';
import { ref, computed } from 'vue';
import router from '@/router';
import { useRoute } from 'vue-router';

export default {
  name: 'PumpOverview',
  components: {
    VPumpOverviewRow,
    VTable,
    VHeaderRow,
    VPumpRetrieveForm,
    PumpSearch
  },
  setup() {
    const store = useStore();
    const user = ref(null);
    const route = useRoute();
    const activePpwRecId = ref(null);

    const fetchResults = () => store.dispatch('navigation/fetchResults');
    currentUserIdentity$.subscribe((value) => (user.value = value));
    activePpwRecId$.subscribe((value) => (activePpwRecId.value = value));
    const reLoadOverview = computed(() => store.getters['pumpOverview/getReLoadOverview']);

    return {
      fetchResults,
      user,
      activePpwRecId,
      reLoadOverview,
      ...mapMutations({
        setReLoadOverview: 'pumpOverview/setReLoadOverview'
      })
    };
  },
  data() {
    return {
      pumpsHeader: ['', 'Omschrijving', 'Serienummer', 'Status', 'Onderhoud nodig in', ''],
      pumpsHeadersForSorting: {
        Omschrijving: 'pumpDesc',
        Serienummer: 'serialNo',
        Status: 'equipmentStatus',
        'Onderhoud nodig in': 'daysLeftForMaintenance'
      },
      selectedPumpToRetrieve: {},
      retrieveOverlay: false,
      isExpanded: false,
      selected: null,
      currentPumpId: null,
      abnormalityRemark: '',
      showReportAbnormality: false,
      updatedPumps: [],
      overviewPumps: {},
      currentPumpData: {},
      userWarehouseId: '',
      pumpsRequireMaintenance: false,
      isPumpOverview: true
    };
  },
  watch: {
    reLoadOverview(value) {
      if (value) {
        this.reloadOverview();
      }
    }
  },
  mounted() {
    this.fetchPumps();
    this.fetchMaintancePumps();
  },
  methods: {
    searchPump(searchDetails) {
      this.computeOverview();
      if (
        (!searchDetails.searchText || (searchDetails && searchDetails.searchText.length < 2)) &&
        searchDetails.status === 'Toon Alles'
      ) {
        return;
      }
      if (searchDetails.searchText && searchDetails.searchText.length > 2) {
        const lowerCaseSearchText = searchDetails.searchText.toLowerCase();
        this.updatedPumps = this.updatedPumps.filter((l) => {
          return (
            l.pumpDesc.toLowerCase().includes(lowerCaseSearchText) ||
            (l.serialNo !== null && l.serialNo.toLowerCase().includes(lowerCaseSearchText))
          );
        });
      }
      if (searchDetails.status && searchDetails.status !== 'Toon Alles') {
        this.updatedPumps = this.updatedPumps.filter((l) => {
          if (searchDetails.status === 'Available') {
            return l.equipmentStatus === searchDetails.status || l.equipmentStatus === 'Lend';
          }
          if (searchDetails.status === 'Allocated') {
            return l.equipmentStatus === searchDetails.status || l.equipmentStatus === 'LendAndAssigned';
          }
          return l.equipmentStatus === searchDetails.status;
        });
      }
    },
    retrievePump(pumpData) {
      this.selectedPumpToRetrieve = this.getSpecificPump(pumpData);
      this.currentPumpData = pumpData;
      this.showRetrieveOverlay();
    },
    fetchPumps() {
      this.fetchResults().then(() => {
        this.fetchOverviewPumps().then(() => {
          this.computeOverview();
        });
      });
    },
    forceResetFilters() {
      router.push('/pompenoverzicht');
      this.fetchPumps();
      this.fetchMaintancePumps();
    },
    computeOverview() {
      var urlEquipmentId = router.currentRoute.value.params.id || '';
      if (urlEquipmentId && urlEquipmentId.trim().length > 0) {
        this.isPumpOverview = false;
      } else {
        this.isPumpOverview = true;
      }
      if (!this.isPumpOverview) {
        this.overviewPumps.allPumps = this.overviewPumps.allPumps.filter(
          (item) => item.equipmentID === parseInt(urlEquipmentId)
        );
      }

      this.updatedPumps = [];
      const initialStatusSorting = { Available: 1, Allocated: 2, 'Naar Mediq': 3, default: Infinity };
      this.updatedPumps = this.overviewPumps.allPumps.map((x) =>
        x.equipmentStatus === 'RequestMaintenance' ||
        x.equipmentStatus === 'RequestRepair' ||
        x.equipmentStatus === 'Pickup' ||
        x.equipmentStatus === 'ToMediq'
          ? { ...x, equipmentStatus: 'Naar Mediq' }
          : x
      );
      this.updatedPumps.sort(function (a, b) {
        return (
          (initialStatusSorting[a.equipmentStatus] || initialStatusSorting.default) -
            (initialStatusSorting[b.equipmentStatus] || initialStatusSorting.default) ||
          a.equipmentStatus.localeCompare(b.equipmentStatus) ||
          a.pumpDesc.localeCompare(b.pumpDesc)
        );
      });
    },
    showRetrieveOverlay() {
      this.retrieveOverlay = true;
    },
    closeRetrieveOverlay() {
      this.selectedPumpToRetrieve = {};
      this.retrieveOverlay = false;
      this.fetchOverviewPumps(this.currentPumpData);
      this.$router.go();
    },
    sortPumpsByField(sortFieldDetails) {
      let key = sortFieldDetails.option;
      let value = this.pumpsHeadersForSorting[key];
      this.updatedPumps.sort(function (a, b) {
        const leftValue = a[value];
        const rightValue = b[value];
        if (!leftValue) {
          return 1;
        }
        if (!rightValue) {
          return -1;
        }
        if (sortFieldDetails.isAscending) {
          if (typeof leftValue === 'number' && typeof rightValue === 'number') {
            return leftValue < rightValue ? -1 : 1;
          } else {
            return leftValue.localeCompare(rightValue);
          }
        } else {
          if (typeof leftValue === 'number' && typeof rightValue === 'number') {
            return leftValue > rightValue ? -1 : 1;
          } else {
            return rightValue.localeCompare(leftValue);
          }
        }
      });
    },
    setRemarks(remarks) {
      this.abnormalityRemark = remarks;
    },
    reportAbnormality() {
      let requiredFields = {
        currentPumpId: this.currentPumpId,
        abnormalityRemark: this.abnormalityRemark
      };
      axios.post('Equipment/EmailReportAbnormality', requiredFields).then((response) => {
        this.showAlert();
        this.abnormalityRemark = '';
        this.currentPumpId = '0';
      });
    },
    toggleReportAbnormality() {
      this.showReportAbnormality = !this.showReportAbnormality;
    },
    setSelectedPumpId(event) {
      if (!event.currentTarget.value) {
        return;
      }
      this.currentPumpId = event.currentTarget.value;
    },
    showAlert() {
      this.$emitter.emit('alert', {
        message: 'Uw bericht is verzonden en wordt zo snel mogelijk opgepakt door Mediq.'
      });
    },
    fetchOverviewPumps() {
      return axios.get('Equipment/GetPumpOverview').then(
        (response) => {
          this.overviewPumps = response.data;
          this.userWarehouseId = response.data.userWarehouseId;
        },
        (error) => {
          return error.response;
        }
      );
    },
    fetchMaintancePumps() {
      return fetchQuery('GetEquipmentRequireMaintenance', { ppwrecId: this.activePpwRecId.value }).then(
        (response) => {
          if (response && response.length > 0) {
            this.pumpsRequireMaintenance = true;
          }
        },
        (error) => {
          return error.response;
        }
      );
    },
    getSpecificPump(pumpData) {
      return this.overviewPumps.allPumps.find((pump) => pump.equipmentID === pumpData.id);
    },
    reloadOverview() {
      this.fetchOverviewPumps().then(() => {
        this.computeOverview();
        this.setReLoadOverview(false);
      });
    }
  }
};
</script>
<style lang="css">
.pumpOverview .overlay > div {
  max-height: 720px;
}
.pumpOverview .pumpOverview__selectPump .form-group label {
  padding-left: 10px;
  margin-top: -8px;
}
.pumpOverview .pumpOverview__sselectPump .form-group--hasvalue label {
  display: none;
}
.pumpOverview .form-group .form-group__details:before {
  background: none;
}
.pumpOverview .form-group .form-group__details:after {
  background: none;
}
.pumpOverview .form-group {
  padding: 0px;
}
.pumpOverview .form-group__icon {
  margin: -5px 10px 0px 0px;
}
</style>
<style lang="scss" scoped>
.pumpOverview {
  table {
    th,
    td {
      vertical-align: baseline;
      text-align: left;
      padding: 12px 20px;
      font-weight: normal;
    }
    a {
      color: $teal-darkest;
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }
    thead {
      padding-bottom: 2px;
      td,
      th {
        background-color: $teal-lighter;
        font-weight: bold;
        white-space: nowrap;
      }
    }
    tbody {
      tr {
        border: 1px solid $gray-light;
      }
      th,
      td {
        padding-top: 17px;
        padding-bottom: 32px;
      }
    }
  }
}
</style>
